import api from './api';
import firstPartyAnalytics from './firstPartyAnalytics';
import { AnalyticsEvents } from './analyticsEvents';
import { t as tr } from '../i18n';
import { PostMedia, parsePostMedia } from '../utils/postMedia';

// ============================================
// Social-Media-Posts (Instagram-artige Beiträge)
//
// Backend-Endpunkte:
//   GET    /api/posts/feed?limit=&offset=              (public, mit dc-sign → likedByMe)
//   GET    /api/posts/for-you?limit=&offset=           (auth)
//   GET    /api/posts/by-author/:authorId?limit=&offset= (public)
//   GET    /api/posts/:id                              (public)
//   POST   /api/posts                                  (auth)
//   DELETE /api/posts/:id                              (auth, Ersteller/Verwalter/Admin)
//   POST   /api/posts/:id/like                         (auth)
//   POST   /api/posts/:id/report                       (auth)
//   POST   /api/users/block | /api/users/unblock       (auth)
//   GET    /api/admin/posts/reported                   (admin)
//   DELETE /api/admin/posts/:id                        (admin)
//
// ALLE Felder werden defensiv/nullable geparst (CLAUDE.md-Regel).
// ============================================

export type PostAuthorType = 'PLAYER' | 'CLUB' | 'PUB' | 'SHOP' | 'TOURNAMENT' | 'SERIES' | 'LEAGUE' | 'ASSOCIATION';

export const POST_AUTHOR_TYPE_LABELS: Record<PostAuthorType, string> = {
  PLAYER: 'Spieler',
  CLUB: 'Verein',
  PUB: tr('web.component.create-entry-modal.spielstaette'),
  SHOP: 'Shop',
  TOURNAMENT: 'Turnier',
  SERIES: 'Serie',
  LEAGUE: 'Liga',
  ASSOCIATION: 'Verband',
};

export type { PostMedia } from '../utils/postMedia';

/** Medien-Eingabe beim Erstellen/Bearbeiten (Feed-Videos 13.10.2026) */
export type PostMediaInput = { type: 'image'; data: string } | { type: 'video'; videoId: string };

/** Antwort von POST /api/posts/video */
export interface UploadedVideo {
  videoId: string;
  file: string;
  poster: string | null;
  duration: number | null;
  width: number | null;
  height: number | null;
  status: 'processing' | 'ready' | 'failed';
  error?: string | null;
}

export interface DartPost {
  id: string;
  authorType?: PostAuthorType | null;
  authorId?: string | null;
  createdByUserId?: string | null;
  authorName?: string | null;
  authorImage?: string | null;
  authorSlug?: string | null;
  images: string[];
  /** Feed-Videos (13.10.2026): geordnete Medien (Bild/Video); bei alten Beiträgen aus images abgeleitet */
  media?: PostMedia[];
  hasVideo?: boolean;
  caption?: string | null;
  likeCount: number;
  commentCount: number;
  status?: string | null;
  createdAt?: number | null;
  likedByMe: boolean;
}

export interface PostReport {
  reporterId?: string | null;
  reason?: string | null;
  createdAt?: number | null;
}

export interface ReportedPost extends DartPost {
  reports: PostReport[];
}

export interface PostListResult {
  posts: DartPost[];
  total: number | null;
}

export interface CreatePostPayload {
  authorType: PostAuthorType;
  authorId: string;
  images?: string[]; // base64-Data-URLs, min 1, max 10 (alt)
  /** Neu: Bild/Video gemischt; hat Vorrang vor images */
  media?: PostMediaInput[];
  caption?: string;
}

const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/+$/, '');

const AUTHOR_TYPES: PostAuthorType[] = ['PLAYER', 'CLUB', 'PUB', 'SHOP', 'TOURNAMENT', 'SERIES', 'LEAGUE', 'ASSOCIATION'];

/** createdAt kann Number (ms/sek) oder ISO-String sein – normalisiert auf ms. */
function parseTimestamp(value: any): number | null {
  if (typeof value === 'number' && value > 0) {
    return value < 1e12 ? value * 1000 : value;
  }
  if (typeof value === 'string' && value.trim()) {
    const t = new Date(value).getTime();
    if (!isNaN(t)) return t;
  }
  return null;
}

/** Einen Post defensiv parsen — alle Felder dürfen fehlen/null sein. */
export function parsePost(data: any): DartPost | null {
  if (!data || typeof data !== 'object') return null;
  const id = data.id || data._id || '';
  if (!id) return null;
  const rawType = typeof data.authorType === 'string' ? data.authorType.toUpperCase() : null;
  const authorType = rawType && AUTHOR_TYPES.includes(rawType as PostAuthorType)
    ? (rawType as PostAuthorType)
    : null;
  const images = Array.isArray(data.images)
    ? data.images.filter((i: any) => typeof i === 'string' && i.trim().length > 0)
    : [];
  return {
    id: String(id),
    authorType,
    authorId: data.authorId ?? null,
    createdByUserId: data.createdByUserId ?? null,
    authorName: typeof data.authorName === 'string' && data.authorName.trim() ? data.authorName : null,
    authorImage: typeof data.authorImage === 'string' && data.authorImage.trim() ? data.authorImage : null,
    authorSlug: typeof data.authorSlug === 'string' && data.authorSlug.trim() ? data.authorSlug : null,
    images,
    media: parsePostMedia(data),
    hasVideo: data.hasVideo === true || parsePostMedia(data).some((m) => m.type === 'video'),
    caption: typeof data.caption === 'string' && data.caption.length > 0 ? data.caption : null,
    likeCount: typeof data.likeCount === 'number' && data.likeCount >= 0 ? data.likeCount : 0,
    commentCount: typeof data.commentCount === 'number' && data.commentCount >= 0 ? data.commentCount : 0,
    status: data.status ?? null,
    createdAt: parseTimestamp(data.createdAt),
    likedByMe: data.likedByMe === true,
  };
}

function parsePostList(payload: any): PostListResult {
  const source = payload?.data ?? payload ?? {};
  const raw = Array.isArray(source?.posts) ? source.posts : Array.isArray(source) ? source : [];
  const posts = raw.map((p: any) => parsePost(p)).filter(Boolean) as DartPost[];
  return {
    posts,
    total: typeof source?.total === 'number' ? source.total : null,
  };
}

/** Report defensiv parsen. */
export function parseReport(data: any): PostReport {
  if (!data || typeof data !== 'object') return { reporterId: null, reason: null, createdAt: null };
  return {
    reporterId: data.reporterId ?? null,
    reason: typeof data.reason === 'string' && data.reason.trim() ? data.reason : null,
    createdAt: parseTimestamp(data.createdAt),
  };
}

/**
 * Bild-URL für Post-Bilder & Avatare (Muster userService.getFileUrl):
 * externe URLs bleiben unverändert, Dateinamen → /api/file/:name.
 */
export function getImageUrl(fileName?: string | null): string | null {
  if (!fileName || typeof fileName !== 'string' || !fileName.trim()) return null;
  const trimmed = fileName.trim();
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
  return `${API_BASE_URL}/api/file/${trimmed}`;
}

/** Relative Zeitangabe (deutsch), analog commentService.formatRelativeTime. */
export function formatRelativeTime(timestamp?: number | null): string {
  if (!timestamp || timestamp <= 0) return '';
  const diff = Date.now() - timestamp;
  if (diff < 60 * 1000) return tr('web.service.comment-service.gerade-eben');
  const minutes = Math.floor(diff / (60 * 1000));
  if (minutes < 60) return tr('web.service.comment-service.vor-min', { minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return tr('web.service.comment-service.vor-std', { hours });
  const days = Math.floor(hours / 24);
  if (days < 7) return days === 1 ? tr('web.service.comment-service.vor-1-tag') : tr('web.service.comment-service.vor-tagen', { days });
  try {
    return new Date(timestamp).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch (_) {
    return '';
  }
}

class PostService {
  /** GET /api/posts/feed — öffentlicher Feed (mit dc-sign → likedByMe). */
  async getFeed(params: { limit?: number; offset?: number } = {}): Promise<PostListResult> {
    const { limit = 10, offset = 0 } = params;
    try {
      const res = await api.get(`/api/posts/feed?limit=${limit}&offset=${offset}`);
      return parsePostList(res?.data);
    } catch (err) {
      console.error('Fehler beim Laden des Feeds:', err);
      return { posts: [], total: null };
    }
  }

  /** GET /api/posts/for-you — personalisierter Feed (auth). */
  async getForYou(params: { limit?: number; offset?: number } = {}): Promise<PostListResult> {
    const { limit = 10, offset = 0 } = params;
    try {
      const res = await api.get(`/api/posts/for-you?limit=${limit}&offset=${offset}`);
      return parsePostList(res?.data);
    } catch (err) {
      console.error('Fehler beim Laden von "Für dich":', err);
      return { posts: [], total: null };
    }
  }

  /** GET /api/posts/by-author/:authorId — Beiträge eines Autors (public). */
  async getPostsByAuthor(
    authorId: string,
    params: { limit?: number; offset?: number } = {}
  ): Promise<PostListResult> {
    if (!authorId) return { posts: [], total: 0 };
    const { limit = 12, offset = 0 } = params;
    try {
      const res = await api.get(
        `/api/posts/by-author/${encodeURIComponent(authorId)}?limit=${limit}&offset=${offset}`
      );
      return parsePostList(res?.data);
    } catch (err) {
      console.error('Fehler beim Laden der Beiträge:', err);
      return { posts: [], total: null };
    }
  }

  /** GET /api/posts/:id — einzelnen Beitrag laden. */
  async getPost(id: string): Promise<DartPost | null> {
    if (!id) return null;
    try {
      const res = await api.get(`/api/posts/${encodeURIComponent(id)}`);
      const source = res?.data?.post ?? res?.data?.data ?? res?.data;
      return parsePost(source);
    } catch (err) {
      console.error('Fehler beim Laden des Beitrags:', err);
      return null;
    }
  }

  /** POST /api/posts — Beitrag erstellen (auth). Wirft bei Fehlern. */
  async createPost(payload: CreatePostPayload): Promise<DartPost | null> {
    const body: any = {
      authorType: payload.authorType,
      authorId: payload.authorId,
    };
    if (payload.media && payload.media.length > 0) body.media = payload.media;
    else body.images = payload.images ?? [];
    if (payload.caption && payload.caption.trim()) body.caption = payload.caption.trim();
    const res = await api.post('/api/posts', body);
    void firstPartyAnalytics.trackEvent(AnalyticsEvents.postCreate, { author_type: String((payload as any)?.authorType ?? 'USER') });
    return parsePost(res?.data?.post ?? res?.data?.data ?? res?.data);
  }

  /** DELETE /api/posts/:id — eigenen Beitrag löschen (auth). Wirft bei Fehlern. */
  /** Beitrag bearbeiten: Caption + optional Bilder (Mix aus vorhandenen
   *  Dateinamen und neuen base64-Data-URLs) → aktualisierter Post oder null */
  async updatePost(id: string, caption: string, images?: string[], media?: PostMediaInput[]): Promise<DartPost | null> {
    try {
      const res = await api.put(`/api/posts/${encodeURIComponent(id)}`, {
        caption,
        ...(media !== undefined ? { media } : images !== undefined ? { images } : {}),
      });
      return parsePost(res?.data?.post);
    } catch (err) {
      console.error('Fehler beim Bearbeiten des Beitrags:', err);
      return null;
    }
  }

  /** POST /api/posts/video — Video als Multipart hochladen (≤ 3 min, ≤ 300 MB), Fortschritt 0..100. Wirft bei Fehlern. */
  async uploadVideo(file: File, onProgress?: (percent: number) => void): Promise<UploadedVideo> {
    const form = new FormData();
    form.append('video', file, file.name || 'video.mp4');
    const res = await api.post('/api/posts/video', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 15 * 60 * 1000,
      onUploadProgress: (e: any) => {
        if (!onProgress) return;
        const total = typeof e?.total === 'number' && e.total > 0 ? e.total : file.size;
        onProgress(Math.max(0, Math.min(100, Math.round(((e?.loaded ?? 0) / total) * 100))));
      },
    });
    const v = res?.data?.video ?? res?.data;
    return {
      videoId: String(v?.videoId ?? ''),
      file: String(v?.file ?? ''),
      poster: typeof v?.poster === 'string' ? v.poster : null,
      duration: typeof v?.duration === 'number' ? v.duration : null,
      width: typeof v?.width === 'number' ? v.width : null,
      height: typeof v?.height === 'number' ? v.height : null,
      status: v?.status === 'ready' ? 'ready' : v?.status === 'failed' ? 'failed' : 'processing',
    };
  }

  /** GET /api/posts/video/:id — Verarbeitungsstatus (processing | ready | failed) */
  async getVideoStatus(videoId: string): Promise<UploadedVideo | null> {
    try {
      const res = await api.get(`/api/posts/video/${encodeURIComponent(videoId)}`);
      const v = res?.data?.video;
      if (!v) return null;
      return {
        videoId: String(v.id ?? videoId), file: String(v.file ?? ''), poster: v.poster ?? null, duration: v.duration ?? null,
        width: v.width ?? null, height: v.height ?? null, status: v.status === 'ready' ? 'ready' : v.status === 'failed' ? 'failed' : 'processing', error: v.error ?? null,
      };
    } catch {
      return null;
    }
  }

  /** Posterbild-URL (normale Bild-Pipeline) */
  posterUrl(name?: string | null): string | null {
    return getImageUrl(name);
  }

  async deletePost(id: string): Promise<void> {
    await api.delete(`/api/posts/${encodeURIComponent(id)}`);
  }

  /** POST /api/posts/:id/like — Like togglen (auth). Null bei Fehler → Rollback. */
  async toggleLike(id: string): Promise<{ liked: boolean; likeCount: number } | null> {
    if (!id) return null;
    try {
      const res = await api.post(`/api/posts/${encodeURIComponent(id)}/like`);
      const data = res?.data;
      if (data && typeof data.liked === 'boolean') {
        return {
          liked: data.liked,
          likeCount: typeof data.likeCount === 'number' ? data.likeCount : 0,
        };
      }
      return null;
    } catch (err) {
      console.error('Fehler beim Liken des Beitrags:', err);
      return null;
    }
  }

  /** POST /api/posts/:id/report — Beitrag melden (auth). */
  async reportPost(id: string, reason?: string): Promise<boolean> {
    if (!id) return false;
    try {
      const body: any = {};
      if (reason && reason.trim()) body.reason = reason.trim();
      const res = await api.post(`/api/posts/${encodeURIComponent(id)}/report`, body);
      return res?.data?.success === true;
    } catch (err) {
      console.error('Fehler beim Melden des Beitrags:', err);
      return false;
    }
  }

  /** POST /api/users/block — Nutzer blockieren (auth). */
  async blockUser(userId: string): Promise<boolean> {
    if (!userId) return false;
    try {
      await api.post('/api/users/block', { userId });
      return true;
    } catch (err) {
      console.error('Fehler beim Blockieren des Nutzers:', err);
      return false;
    }
  }

  /** POST /api/users/unblock — Blockierung aufheben (auth). */
  async unblockUser(userId: string): Promise<boolean> {
    if (!userId) return false;
    try {
      await api.post('/api/users/unblock', { userId });
      return true;
    } catch (err) {
      console.error('Fehler beim Entblocken des Nutzers:', err);
      return false;
    }
  }

  /** GET /api/admin/posts/reported — gemeldete Beiträge (admin). */
  async getReportedPosts(): Promise<ReportedPost[]> {
    try {
      const res = await api.get('/api/admin/posts/reported');
      const source = res?.data?.data ?? res?.data ?? {};
      const raw = Array.isArray(source?.posts) ? source.posts : Array.isArray(source) ? source : [];
      return raw
        .map((p: any) => {
          const post = parsePost(p);
          if (!post) return null;
          const reports = Array.isArray(p?.reports) ? p.reports.map(parseReport) : [];
          return { ...post, reports } as ReportedPost;
        })
        .filter(Boolean) as ReportedPost[];
    } catch (err) {
      console.error('Fehler beim Laden der gemeldeten Beiträge:', err);
      return [];
    }
  }

  /** DELETE /api/admin/posts/:id — Beitrag als Admin entfernen. Wirft bei Fehlern. */
  async adminDeletePost(id: string): Promise<void> {
    await api.delete(`/api/admin/posts/${encodeURIComponent(id)}`);
  }
}

const postService = new PostService();
export default postService;
